import { useDispatch, useSelector } from 'react-redux'
import { Box, Collapse, Tab, Tabs, useTheme } from '@mui/material'
import { ListResponseType } from 'services/types'
import { TBakersRecommendation } from 'types/models/BakersRecommendations'
import { TBakersInsight } from 'types/models/BakersInsights'
import { TModuleKey, toggleTab, TTabKey } from 'store/slices/insightsTabsSlice'
import {
  selectIsAnyOpen,
  selectTabs,
} from 'store/selectors/insightsTabsSliceSelectors'
import { InsightsSection } from './InsightsSection'
import { RecommendationsSection } from '../recommentations/RecommendationsSection'
import * as sxStyles from './InsightsAndRecommendations.styled'

interface IInsightsAndRecommendationsProps {
  selectedDeliveryId: string
  moduleKey: TModuleKey
  recommendations?: ListResponseType<TBakersRecommendation>
  insights?: ListResponseType<TBakersInsight>
  isRecommendationsLoading?: boolean
  isInsightsLoading?: boolean
}

export const InsightsAndRecommendations = ({
  selectedDeliveryId,
  moduleKey,
  recommendations,
  isRecommendationsLoading,
  insights,
  isInsightsLoading,
}: IInsightsAndRecommendationsProps) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const tabs = useSelector(selectTabs(moduleKey, selectedDeliveryId))

  const isAnyTabOpen = useSelector(
    selectIsAnyOpen(moduleKey, selectedDeliveryId)
  )

  const handleToggle = (key: TTabKey) => {
    dispatch(
      toggleTab({ module: moduleKey, deliveryId: selectedDeliveryId, tab: key })
    )
  }

  return (
    <Box
      sx={{
        width: isAnyTabOpen ? 350 : 48,
        ...sxStyles.boxWrapper,
      }}
    >
      <Tabs
        orientation="vertical"
        value={false}
        textColor="inherit"
        variant="scrollable"
        sx={sxStyles.tabsStyles}
      >
        <Tab
          label={
            <Box>
              Recommendations{' '}
              <span
                style={{ color: theme.palette.primary.main, fontWeight: 800 }}
              >
                ({recommendations?.results?.length ?? 0})
              </span>
            </Box>
          }
          onClick={() => handleToggle('recommendations')}
          sx={{
            borderLeft: tabs.recommendations
              ? (theme) => `6px solid ${theme.palette.primary.main}`
              : `6px solid ${theme.palette.grey[400]}`,
            ...sxStyles.tabsMargin,
          }}
        />
        <Tab
          label={
            <Box>
              Insights{' '}
              <span
                style={{ color: theme.palette.primary.main, fontWeight: 800 }}
              >
                ({insights?.results?.length ?? 0})
              </span>
            </Box>
          }
          onClick={() => handleToggle('insights')}
          sx={{
            borderLeft: tabs.insights
              ? (theme) => `6px solid ${theme.palette.primary.main}`
              : `6px solid ${theme.palette.grey[400]}`,
            ...sxStyles.tabsMargin,
          }}
        />
      </Tabs>

      {/* Expandable panel */}
      <Box
        sx={{
          width: isAnyTabOpen ? 350 : 0,
          ...sxStyles.panelBoxStyles,
        }}
      >
        <Collapse in={tabs.recommendations}>
          <RecommendationsSection
            recommendations={recommendations}
            isRecommendationLoading={isRecommendationsLoading}
            selectedDeliveryId={selectedDeliveryId}
            moduleKey={moduleKey}
          />
        </Collapse>
        <Collapse in={tabs.insights}>
          <InsightsSection
            insights={insights}
            isInsightLoading={isInsightsLoading}
            selectedDeliveryId={selectedDeliveryId}
            moduleKey={moduleKey}
          />
        </Collapse>
      </Box>
    </Box>
  )
}
